import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface ManagedCity {
  id: string;
  name: string;
  region: string;
  shortDesc: string;
  description: string;
  image: string;
  highlights: string;
  bestSeason: string;
  featured: boolean;
  createdAt: string;
}

const STATIC_REGIONS = [
  {
    id: "karadeniz",
    name: "Karadeniz",
    region: "Karadeniz Bölgesi",
    shortDesc: "Yemyeşil yaylalar, sis bulutları ve şelale vadileriyle Türkiye'nin en mistik bölgesi.",
    image: "https://readdy.ai/api/search-image?query=Black%20Sea%20region%20Turkey%20lush%20green%20mountains%20misty%20valleys%20highland%20plateau%20Karadeniz%20dramatic%20landscape%20fog%20clouds%20emerald%20nature&width=800&height=500&seq=region-karadeniz-guide&orientation=landscape",
    highlights: "Ayder Yaylası, Uzungöl, Sümela Manastırı, Kaçkar Dağları",
    bestSeason: "Yaz & Sonbahar",
    featured: true,
    path: "/guides/karadeniz-yayla-rehberi",
  },
  {
    id: "ege",
    name: "Ege",
    region: "Ege Bölgesi",
    shortDesc: "Antik kentler, zeytin bahçeleri ve turkuaz koylarla dolu Ege'nin eşsiz güzellikleri.",
    image: "https://readdy.ai/api/search-image?query=Aegean%20Turkey%20turquoise%20sea%20ancient%20ruins%20olive%20groves%20Ephesus%20Bodrum%20Ala%C3%A7at%C4%B1%20windmills%20white%20buildings%20Mediterranean%20summer&width=800&height=500&seq=region-ege-guide&orientation=landscape",
    highlights: "Efes, Alaçatı, Bodrum, Pamukkale, Şirince",
    bestSeason: "İlkbahar & Yaz",
    featured: true,
    path: "/places",
  },
  {
    id: "akdeniz",
    name: "Akdeniz",
    region: "Akdeniz Bölgesi",
    shortDesc: "Antik Likya yolları, turkuaz koylar ve Toros dağlarının gölgesinde eşsiz bir deneyim.",
    image: "https://readdy.ai/api/search-image?query=Mediterranean%20Turkey%20Antalya%20turquoise%20coast%20ancient%20Lycian%20ruins%20cliffs%20sea%20Fethiye%20%C3%96l%C3%BCdeniz%20blue%20lagoon%20warm%20sunny&width=800&height=500&seq=region-akdeniz-guide&orientation=landscape",
    highlights: "Antalya, Fethiye, Ölüdeniz, Kaş, Likya Yolu",
    bestSeason: "İlkbahar & Yaz",
    featured: false,
    path: "/places",
  },
  {
    id: "ic-anadolu",
    name: "İç Anadolu",
    region: "İç Anadolu Bölgesi",
    shortDesc: "Kapadokya'nın peri bacaları, tuz gölleri ve bozkırın ortasında saklı antik şehirler.",
    image: "https://readdy.ai/api/search-image?query=Cappadocia%20Turkey%20fairy%20chimneys%20hot%20air%20balloons%20sunrise%20dramatic%20landscape%20rock%20formations%20G%C3%B6reme%20valleys%20unique%20geological%20formations&width=800&height=500&seq=region-ic-anadolu-guide&orientation=landscape",
    highlights: "Kapadokya, Tuz Gölü, Konya, Ankara, Göreme",
    bestSeason: "İlkbahar & Sonbahar",
    featured: false,
    path: "/places",
  },
  {
    id: "guneydogu",
    name: "Güneydoğu",
    region: "Güneydoğu Anadolu",
    shortDesc: "Mezopotamya'nın kadim mirası, taş evler ve Türkiye'nin en zengin mutfak kültürü.",
    image: "https://readdy.ai/api/search-image?query=Southeastern%20Turkey%20Mardin%20ancient%20stone%20city%20Mesopotamia%20plains%20golden%20sunset%20Gaziantep%20bazaar%20traditional%20architecture%20historic&width=800&height=500&seq=region-guneydogu-guide&orientation=landscape",
    highlights: "Mardin, Gaziantep, Şanlıurfa, Nemrut, Diyarbakır",
    bestSeason: "İlkbahar & Sonbahar",
    featured: false,
    path: "/places",
  },
  {
    id: "dogu-anadolu",
    name: "Doğu Anadolu",
    region: "Doğu Anadolu Bölgesi",
    shortDesc: "Van Gölü'nün mavisi, Ağrı Dağı'nın heybeti ve Kars'ın karlı masalıyla keşfedilmeyi bekleyen doğu.",
    image: "https://readdy.ai/api/search-image?query=Eastern%20Turkey%20Van%20Lake%20mountain%20Agri%20Kars%20snowy%20landscape%20dramatic%20nature%20Ani%20ruins%20Armenian%20heritage%20vast%20plateau&width=800&height=500&seq=region-dogu-anadolu-guide&orientation=landscape",
    highlights: "Van Gölü, Ağrı Dağı, Kars, Ani Harabeleri, Doğubayazıt",
    bestSeason: "Yaz",
    featured: false,
    path: "/places",
  },
];

const SEASON_COLORS: Record<string, string> = {
  "İlkbahar": "bg-emerald-100 text-emerald-700",
  "Yaz": "bg-amber-100 text-amber-700",
  "Sonbahar": "bg-orange-100 text-orange-700",
  "Kış": "bg-sky-100 text-sky-700",
  "İlkbahar & Yaz": "bg-emerald-100 text-emerald-700",
  "Yaz & Sonbahar": "bg-amber-100 text-amber-700",
  "İlkbahar & Sonbahar": "bg-lime-100 text-lime-700",
  "Tüm Yıl": "bg-stone-100 text-stone-600",
};

const RegionGuideSection = () => {
  const navigate = useNavigate();
  const [adminCities, setAdminCities] = useState<ManagedCity[]>([]);
  const [activeRegion, setActiveRegion] = useState<string>("Tümü");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("adminCities") || "[]");
      setAdminCities(stored);
    } catch {
      setAdminCities([]);
    }
  }, []);

  const regions = ["Tümü", "Karadeniz", "Ege", "Akdeniz", "İç Anadolu", "Güneydoğu", "Doğu Anadolu", "Marmara"];

  const allItems = [
    ...STATIC_REGIONS,
    ...adminCities.map((c) => ({
      id: c.id,
      name: c.name,
      region: c.region,
      shortDesc: c.shortDesc,
      image: c.image,
      highlights: c.highlights,
      bestSeason: c.bestSeason,
      featured: c.featured,
      path: "/places",
    })),
  ];

  const filtered = activeRegion === "Tümü"
    ? allItems
    : allItems.filter((item) => item.region.includes(activeRegion));

  const featuredItems = allItems.filter((i) => i.featured).slice(0, 2);
  const gridItems = filtered.filter((i) => !i.featured || activeRegion !== "Tümü");
  const displayGrid = activeRegion === "Tümü" ? allItems.filter((i) => !i.featured) : filtered;

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-widest">Bölge Rehberleri</span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Türkiye'yi<br />Bölge Bölge Keşfet
            </h2>
            <p className="text-stone-500 text-sm mt-3 max-w-md leading-relaxed">
              Her bölgenin kendine özgü doğası, kültürü ve lezzetleriyle Türkiye'nin tüm güzelliklerini keşfedin.
            </p>
          </div>

          {/* Region Filter Pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {regions.map((r) => (
              <button
                key={r}
                onClick={() => setActiveRegion(r)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                  ${activeRegion === r
                    ? "bg-stone-900 text-white"
                    : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
                  }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Featured 2 large cards — only on "Tümü" */}
        {activeRegion === "Tümü" && featuredItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {featuredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(item.path)}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="relative h-72 rounded-2xl overflow-hidden cursor-pointer group"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className={`w-full h-full object-cover object-top transition-transform duration-700 ${hoveredId === item.id ? "scale-105" : "scale-100"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                {/* Season badge */}
                <div className="absolute top-4 left-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${SEASON_COLORS[item.bestSeason] || "bg-stone-100 text-stone-600"}`}>
                    <i className="ri-sun-line mr-1"></i>{item.bestSeason}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider mb-1">{item.region}</p>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.name}
                  </h3>
                  <p className="text-stone-300 text-sm mb-3 line-clamp-2">{item.shortDesc}</p>
                  {item.highlights && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <i className="ri-map-pin-line text-amber-400 text-xs"></i>
                      <span className="text-stone-400 text-xs">
                        {item.highlights.split(",").slice(0, 3).join(" · ")}
                      </span>
                    </div>
                  )}
                  <div className={`mt-4 flex items-center gap-1.5 text-amber-400 text-sm font-semibold transition-all duration-300 ${hoveredId === item.id ? "translate-x-1" : ""}`}>
                    <span>Rehberi Keşfet</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid of smaller cards */}
        {displayGrid.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {displayGrid.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(item.path)}
                onMouseEnter={() => setHoveredId(item.id + "-grid")}
                onMouseLeave={() => setHoveredId(null)}
                className="relative h-56 rounded-xl overflow-hidden cursor-pointer group"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className={`w-full h-full object-cover object-top transition-transform duration-500 ${hoveredId === item.id + "-grid" ? "scale-110" : "scale-100"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                <div className="absolute top-3 right-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEASON_COLORS[item.bestSeason] || "bg-stone-100 text-stone-600"}`}>
                    {item.bestSeason}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-amber-300 text-xs font-medium mb-0.5">{item.region}</p>
                  <h4 className="text-white font-bold text-lg leading-tight mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.name}
                  </h4>
                  <p className="text-stone-300 text-xs line-clamp-2 mb-2">{item.shortDesc}</p>
                  {item.highlights && (
                    <p className="text-stone-400 text-xs line-clamp-1">
                      <i className="ri-map-pin-line text-amber-400 mr-1"></i>
                      {item.highlights.split(",").slice(0, 2).join(" · ")}
                    </p>
                  )}
                </div>

                {/* Hover overlay */}
                <div className={`absolute inset-0 bg-amber-500/10 transition-opacity duration-300 ${hoveredId === item.id + "-grid" ? "opacity-100" : "opacity-0"}`}></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-map-2-line text-stone-300 text-3xl"></i>
            </div>
            <p className="text-stone-400 text-sm">Bu bölge için henüz içerik eklenmedi.</p>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-2xl border border-stone-100 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
              <i className="ri-compass-3-line text-amber-500 text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900">Karadeniz Yayla Rehberi</p>
              <p className="text-xs text-stone-400 mt-0.5">6 detaylı rota · Ayder, Uzungöl, Pokut ve daha fazlası</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/guides/karadeniz-yayla-rehberi")}
            className="flex items-center gap-2 px-5 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-full hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Rehberi Gör <i className="ri-arrow-right-line"></i>
          </button>
        </div>

      </div>
    </section>
  );
};

export default RegionGuideSection;
